/**
 * Top app bar, modelled on the Auth0 dashboard: org/tenant selector + region on
 * the left, a command-palette search in the middle, and the action cluster on
 * the right — "Discuss your needs", Documentation, AI, notifications, wallet,
 * theme and the user menu. Each control is its own component under
 * `components/shell/` so this file stays a thin layout.
 */
import * as React from "react";
import { MessageSquareText, BookOpen, Menu, Sparkles } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Button, LatestReleaseBadge, ThemeToggle } from "@authe/ui-core";
import { EXTERNAL_LINKS, ROUTES } from "@/config";
import { useAskAi } from "@/app/ask-ai-shell";
import { useBlueRouting } from "@/app/use-blue-routing";
import { OrgTenantMenu } from "./OrgTenantMenu";
import { HeaderSearch } from "./HeaderSearch";
import { NotificationsMenu } from "./NotificationsMenu";
import { WalletButton } from "./WalletButton";
import { ProfileMenu } from "./ProfileMenu";
import { Hint } from "./Hint";
import { ChatDrawer } from "./ChatDrawer";

export interface HeaderProps {
  /**
   * Opens the navigation drawer. Below 768px the sidebar is not on screen, so
   * the header carries the one button that reaches it.
   */
  onOpenNav?: () => void;
}

export function Header({ onOpenNav }: HeaderProps = {}): React.ReactElement {
  const [chatOpen, setChatOpen] = React.useState(false);
  const { openAskAi } = useAskAi();
  // Chrome-level, because it is true of the whole session rather than of any one
  // page: a tester on the latest release carries it between routes, and it is
  // the first thing that explains "why does this behave differently?". Renders
  // nothing for everyone else — the live release is the default and is never
  // labelled.
  const { summary: latestRelease } = useBlueRouting();
  const navigate = useNavigate();

  return (
    // Wraps rather than clips.
    //
    // The shell is `h-screen overflow-hidden` and this bar was `h-14` with a
    // `shrink-0` cluster at each end, so its content had a hard minimum width
    // and nowhere to put the excess. Measured at a 640px viewport — which is
    // what a 1280px display looks like at the 200% zoom WCAG reflow asks for —
    // the right-hand cluster ran to `right: 975px` while
    // `documentElement.scrollWidth` stayed 640: the notification count, the
    // wallet, the theme toggle and the profile menu (the only route to sign
    // out) were off-screen with no scrollbar to reach them. Nothing was
    // broken for the keyboard, which tabs to them regardless — this was
    // pointer-only, and invisible to anyone testing with a keyboard.
    //
    // `flex-wrap` + a minimum height instead of a fixed one is the whole fix:
    // above ~1100px it is the same single row it always was, and below that the
    // cluster takes a second row rather than leaving the viewport.
    <header className="flex min-h-14 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-background px-4 py-2">
      <div className="flex shrink-0 items-center gap-1">
        {onOpenNav ? (
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Open navigation"
            onClick={onOpenNav}
          >
            <Menu className="h-5 w-5" />
          </Button>
        ) : null}
        <OrgTenantMenu />
      </div>

      <div className="flex min-w-0 flex-1 justify-center">
        <HeaderSearch />
      </div>

      {/* `ml-auto` keeps it against the right edge on whichever row it lands
          on, and it wraps internally too so a narrower viewport still stacks
          instead of overflowing its own row. */}
      <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1.5">
        {/* First in the cluster: it is the one control here that changes how
            everything else behaves, and the marketing button hides below `xl`. */}
        <LatestReleaseBadge
          summary={latestRelease}
          onSelect={() => void navigate({ to: ROUTES.settingsAccess })}
        />

        <Button
          variant="default"
          size="sm"
          // `xl`, not `lg`. This is the widest control in the cluster (~190px)
          // and at `lg` it appeared at exactly the width where the row could no
          // longer hold it: measured at a 1024px viewport the cluster came to
          // 761px against 752px of content box, so revealing the button was
          // itself what pushed the bar onto two rows. At `xl` the single-row
          // bar survives a 1280px display, and a 1024px one keeps the six
          // controls that are not a marketing prompt.
          className="hidden xl:inline-flex"
          onClick={() => setChatOpen(true)}
        >
          <MessageSquareText className="h-4 w-4" />
          Discuss your needs
        </Button>
        <ChatDrawer open={chatOpen} onOpenChange={setChatOpen} />

        <Hint label="Documentation">
          <Button asChild variant="ghost" size="icon" aria-label="Documentation">
            <a href={EXTERNAL_LINKS.docs} target="_blank" rel="noreferrer">
              <BookOpen className="h-4 w-4" />
            </a>
          </Button>
        </Hint>

        <Hint label="Ask AI">
          <Button variant="ghost" size="icon" aria-label="Ask AI" onClick={openAskAi}>
            <Sparkles className="h-4 w-4" />
          </Button>
        </Hint>

        <NotificationsMenu />

        <WalletButton />

        {/*
          The shared toggle, not a fourth hand-rolled one (both share viewers
          had their own too). It also fixes what this copy got wrong: it
          flipped off `mode`, the STORED preference, which can be `"system"` —
          so a viewer on a dark OS with the default preference saw a sun,
          clicked it, and got `setMode("dark")`, the theme already on screen.
          The first click did nothing. The shared one inverts `resolvedMode`.
        */}
        <ThemeToggle />

        <ProfileMenu />
      </div>
    </header>
  );
}
